import { useEffect, useMemo } from 'react'
import moment from 'moment'
import {
  useDashboardFetchWaves,
  useReportDashboardFetchWave,
  waveFetchState,
} from '@context/DashboardFetchWaveContext'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  buildDashboardDetailHref,
  DASHBOARD_DETAIL_CONTEXT,
} from '@constants/Dashboard/dashboardDetailContext.util'
import { DASHBOARD_DETAIL_HREFS } from '@constants/Dashboard/dashboardDetailRoutes.constants'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useChairTimeOverview } from '@hooks/dashboard/useChairTimeOverview'
import { useDiagnosisOverview } from '@hooks/dashboard/useDiagnosisOverview'
import { useIncompleteTreatmentOverview } from '@hooks/dashboard/useIncompleteTreatmentOverview'
import { useNoShowRateOverview } from '@hooks/dashboard/useNoShowRateOverview'
import { useOpenTreatmentOverview } from '@hooks/dashboard/useOpenTreatmentOverview'
import { useOverviewAlerts } from '@hooks/dashboard/useOverviewAlerts'
import { usePatientFlowOverview } from '@hooks/dashboard/usePatientFlowOverview'
import { useProceduresOverview } from '@hooks/dashboard/useProcedures'
import { useCollectionsOverview } from '@hooks/dashboard/useCollections'
import { useARAgingOverview } from '@hooks/dashboard/useARAging'
import { useProductionOverviewDetail } from '@hooks/dashboard/useProductionOverviewDetail'
import { useRecallOverview } from '@hooks/dashboard/useRecallOverview'
import { useRecareOverview } from '@hooks/dashboard/useRecareOverview'
import { useSameDayCancellationOverview } from '@hooks/dashboard/useSameDayCancellation'
import { useTodayScheduleAppointments } from '@hooks/dashboard/useTodayScheduleAppointments'
import { useTreatmentAcceptanceOverview } from '@hooks/dashboard/useTreatmentAcceptanceOverview'
import { CALENDAR_DATE_FORMAT } from '@utils/dashboardFinance.util'
import { createFailedToast } from '@utils/notificationToast.util'
import {
  buildOverviewSectionWaves,
  type OverviewSectionWaves,
} from './overviewSectionWaves'
import { useOverviewUtilization } from './useOverviewUtilization'

export type { OverviewSectionWaves } from './overviewSectionWaves'

type UseOverviewDashboardOptions = {
  range: RangeKey
  date: string
  hiddenSections: Set<string>
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- inferred; exported hook is typed below
function useOverviewDashboardImpl({
  range,
  date,
  hiddenSections,
}: UseOverviewDashboardOptions) {
  const waves: OverviewSectionWaves = buildOverviewSectionWaves(hiddenSections)
  const openWaves = useDashboardFetchWaves()
  const proceduresGate = waveFetchState(openWaves, waves.procedures)
  const collectionsGate = waveFetchState(openWaves, waves.collections)
  const arAgingGate = waveFetchState(openWaves, waves.arAging)
  const productionGate = waveFetchState(openWaves, waves.production)
  const todayScheduleGate = waveFetchState(openWaves, waves.todaySchedule)
  const patientFlowGate = waveFetchState(openWaves, waves.patientFlow)
  const incompleteTreatmentGate = waveFetchState(
    openWaves,
    waves.incompleteTreatment,
  )
  const openTreatmentGate = waveFetchState(openWaves, waves.openTreatment)
  const recallGate = waveFetchState(openWaves, waves.recall)
  const chairTimeGate = waveFetchState(openWaves, waves.chairTime)
  const noShowRateGate = waveFetchState(openWaves, waves.noShowRate)
  const sameDayCancellationGate = waveFetchState(
    openWaves,
    waves.sameDayCancellation,
  )
  const alertsGate = waveFetchState(openWaves, waves.alerts)
  const recareGate = waveFetchState(openWaves, waves.recare)
  const diagnosisGate = waveFetchState(openWaves, waves.diagnosis)
  const treatmentAcceptanceGate = waveFetchState(
    openWaves,
    waves.treatmentAcceptance,
  )
  const utilization = useOverviewUtilization({
    range,
    date,
    hiddenSections,
    waves,
  })
  const proceduresApi = useProceduresOverview({
    range,
    date,
    enabled: proceduresGate.enabled,
    queued: proceduresGate.queued,
  })
  const collectionsApi = useCollectionsOverview({
    range,
    date,
    enabled: collectionsGate.enabled,
    queued: collectionsGate.queued,
  })
  const arAgingApi = useARAgingOverview({
    range,
    date,
    enabled: arAgingGate.enabled,
    queued: arAgingGate.queued,
  })
  const productionApi = useProductionOverviewDetail({
    range,
    date,
    enabled: productionGate.enabled,
    queued: productionGate.queued,
  })
  const todayScheduleApi = useTodayScheduleAppointments(
    date,
    todayScheduleGate.enabled,
    todayScheduleGate.queued,
  )
  const patientFlowApi = usePatientFlowOverview(
    date,
    patientFlowGate.enabled,
    patientFlowGate.queued,
  )
  const incompleteTreatmentApi = useIncompleteTreatmentOverview(
    range,
    date,
    incompleteTreatmentGate.enabled,
    incompleteTreatmentGate.queued,
  )
  const openTreatmentApi = useOpenTreatmentOverview(
    range,
    date,
    openTreatmentGate.enabled,
    openTreatmentGate.queued,
  )
  const recallApi = useRecallOverview(
    range,
    date,
    recallGate.enabled,
    recallGate.queued,
  )
  const chairTimeApi = useChairTimeOverview(
    range,
    date,
    chairTimeGate.enabled,
    chairTimeGate.queued,
  )
  const noShowRateApi = useNoShowRateOverview(
    range,
    date,
    noShowRateGate.enabled,
    noShowRateGate.queued,
  )
  const sameDayCancellationApi = useSameDayCancellationOverview(
    range,
    date,
    sameDayCancellationGate.enabled,
    sameDayCancellationGate.queued,
  )
  const alertsApi = useOverviewAlerts(alertsGate.enabled, alertsGate.queued)
  const recareApi = useRecareOverview(
    range,
    date,
    recareGate.enabled,
    recareGate.queued,
  )
  const diagnosisApi = useDiagnosisOverview(
    range,
    date,
    diagnosisGate.enabled,
    diagnosisGate.queued,
  )
  const treatmentAcceptanceApi = useTreatmentAcceptanceOverview(
    range,
    date,
    treatmentAcceptanceGate.enabled,
    treatmentAcceptanceGate.queued,
  )

  const hrefs = useMemo(() => {
    const detailParams = {
      context: DASHBOARD_DETAIL_CONTEXT.overview,
      range,
      date: moment(date, CALENDAR_DATE_FORMAT).format('YYYY-MM-DD'),
    }
    return {
      procedures: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.procedures,
        detailParams,
      ),
      collection: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.collection,
        detailParams,
      ),
      production: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.productionOverview,
        detailParams,
      ),
      arAging: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.arAgingOverview,
        detailParams,
      ),
    }
  }, [range, date])

  const loading =
    (proceduresGate.enabled && proceduresApi.loading) ||
    (collectionsGate.enabled && collectionsApi.loading) ||
    (arAgingGate.enabled && arAgingApi.loading) ||
    (productionGate.enabled && productionApi.loading) ||
    (todayScheduleGate.enabled && todayScheduleApi.loading) ||
    (patientFlowGate.enabled && patientFlowApi.loading) ||
    (incompleteTreatmentGate.enabled && incompleteTreatmentApi.loading) ||
    (openTreatmentGate.enabled && openTreatmentApi.loading) ||
    (recallGate.enabled && recallApi.loading) ||
    (chairTimeGate.enabled && chairTimeApi.loading) ||
    (noShowRateGate.enabled && noShowRateApi.loading) ||
    (sameDayCancellationGate.enabled && sameDayCancellationApi.loading) ||
    (alertsGate.enabled && alertsApi.loading) ||
    (recareGate.enabled && recareApi.loading) ||
    (diagnosisGate.enabled && diagnosisApi.loading) ||
    (treatmentAcceptanceGate.enabled && treatmentAcceptanceApi.loading) ||
    utilization.schedulingLoading ||
    utilization.clinicalEfficiencyLoading ||
    utilization.diagnosedTreatmentLoading ||
    utilization.caseAcceptanceLoading ||
    utilization.providerUtilizationLoading

  const hasError =
    (proceduresGate.enabled &&
      !proceduresApi.loading &&
      !!proceduresApi.error) ||
    (collectionsGate.enabled &&
      !collectionsApi.loading &&
      !!collectionsApi.error) ||
    (arAgingGate.enabled && !arAgingApi.loading && !!arAgingApi.error) ||
    (productionGate.enabled &&
      !productionApi.loading &&
      !!productionApi.error) ||
    (todayScheduleGate.enabled &&
      !todayScheduleApi.loading &&
      !!todayScheduleApi.error) ||
    (patientFlowGate.enabled &&
      !patientFlowApi.loading &&
      !!patientFlowApi.error) ||
    (incompleteTreatmentGate.enabled &&
      !incompleteTreatmentApi.loading &&
      !!incompleteTreatmentApi.error) ||
    (openTreatmentGate.enabled &&
      !openTreatmentApi.loading &&
      !!openTreatmentApi.error) ||
    (recallGate.enabled && !recallApi.loading && !!recallApi.error) ||
    (chairTimeGate.enabled && !chairTimeApi.loading && !!chairTimeApi.error) ||
    (noShowRateGate.enabled &&
      !noShowRateApi.loading &&
      !!noShowRateApi.error) ||
    (sameDayCancellationGate.enabled &&
      !sameDayCancellationApi.loading &&
      !!sameDayCancellationApi.error) ||
    (alertsGate.enabled && !alertsApi.loading && !!alertsApi.error) ||
    (recareGate.enabled && !recareApi.loading && !!recareApi.error) ||
    (diagnosisGate.enabled && !diagnosisApi.loading && !!diagnosisApi.error) ||
    (treatmentAcceptanceGate.enabled &&
      !treatmentAcceptanceApi.loading &&
      !!treatmentAcceptanceApi.error)

  useReportDashboardFetchWave(
    0,
    (waves.production === 0 && productionApi.loading) ||
      (waves.procedures === 0 && proceduresApi.loading) ||
      (waves.collections === 0 && collectionsApi.loading) ||
      (waves.arAging === 0 && arAgingApi.loading) ||
      (waves.todaySchedule === 0 && todayScheduleApi.loading),
  )
  useReportDashboardFetchWave(
    1,
    (waves.procedures === 1 && proceduresApi.loading) ||
      (waves.production === 1 && productionApi.loading) ||
      (waves.collections === 1 && collectionsApi.loading) ||
      (waves.arAging === 1 && arAgingApi.loading) ||
      (waves.patientFlow === 1 && patientFlowApi.loading) ||
      (waves.incompleteTreatment === 1 && incompleteTreatmentApi.loading) ||
      (waves.openTreatment === 1 && openTreatmentApi.loading),
  )
  useReportDashboardFetchWave(
    2,
    (waves.production === 2 && productionApi.loading) ||
      (waves.collections === 2 && collectionsApi.loading) ||
      (waves.arAging === 2 && arAgingApi.loading) ||
      (waves.procedures === 2 && proceduresApi.loading) ||
      (waves.recall === 2 && recallApi.loading) ||
      (waves.chairTime === 2 && chairTimeApi.loading) ||
      (waves.noShowRate === 2 && noShowRateApi.loading) ||
      (waves.sameDayCancellation === 2 && sameDayCancellationApi.loading) ||
      (waves.alerts === 2 && alertsApi.loading) ||
      (waves.recare === 2 && recareApi.loading) ||
      (waves.diagnosis === 2 && diagnosisApi.loading) ||
      (waves.treatmentAcceptance === 2 && treatmentAcceptanceApi.loading),
  )

  const { showToast } = useNotificationToast()

  useEffect(() => {
    if (!loading && hasError) {
      showToast(
        createFailedToast(
          'Failed to load metric',
          'Failed to load overview dashboard data. Please try again.',
        ),
      )
    }
  }, [loading, hasError, showToast])

  return {
    range,
    hrefs,
    loading,
    hasError,
    proceduresApi,
    collectionsApi,
    arAgingApi,
    productionApi,
    todayScheduleApi,
    patientFlowApi,
    incompleteTreatmentApi,
    openTreatmentApi,
    recallApi,
    chairTimeApi,
    noShowRateApi,
    sameDayCancellationApi,
    alertsApi,
    recareApi,
    diagnosisApi,
    treatmentAcceptanceApi,
    utilization,
  }
}

export type OverviewDashboard = ReturnType<typeof useOverviewDashboardImpl>

export function useOverviewDashboard(
  options: UseOverviewDashboardOptions,
): OverviewDashboard {
  return useOverviewDashboardImpl(options)
}
